import { extractCreatorId } from '../admin/utils/authUtils';
import { readCreatorStorefrontFromStorage } from './creatorStorefront';

/** Merge populated `createdBy` objects without dropping `creatorSlug`. */
export function mergePopulatedCreatedBy(preferred, fallback) {
  if (preferred == null && fallback == null) return null;
  if (typeof preferred === 'string' && !fallback) return preferred;
  if (typeof fallback === 'string' && !preferred) return fallback;
  const a = typeof preferred === 'object' && preferred ? preferred : null;
  const b = typeof fallback === 'object' && fallback ? fallback : null;
  if (!a) return b;
  if (!b) return a;
  const id = extractCreatorId(a) || extractCreatorId(b);
  const slug =
    normalizeCreatorSlugInput(a.creatorSlug) ||
    normalizeCreatorSlugInput(b.creatorSlug) ||
    '';
  return {
    ...b,
    ...a,
    ...(id ? { _id: id, id } : {}),
    ...(slug ? { creatorSlug: slug } : {}),
    name: a.name || b.name,
    role: a.role || b.role,
    email: a.email || b.email,
  };
}

/** In-memory slug cache (filled when public profiles load). */
const slugCacheByCreatorId = new Map();

export function rememberCreatorSlug(creatorId, slug) {
  const id = String(creatorId || '').trim();
  const s = normalizeCreatorSlugInput(slug);
  if (id && s) slugCacheByCreatorId.set(id, s);
}

export function suggestCreatorSlugFromName(name) {
  return normalizeCreatorSlugInput(name);
}

/** Resolve shop handle from API object, cache, or team localStorage. */
export function resolveCreatorSlug(createdByOrId) {
  if (createdByOrId == null || createdByOrId === '') return '';

  let id = '';
  if (typeof createdByOrId === 'string') {
    id = createdByOrId.trim();
  } else if (typeof createdByOrId === 'object') {
    id = extractCreatorId(createdByOrId) || '';
    const fromObj = normalizeCreatorSlugInput(createdByOrId.creatorSlug);
    if (fromObj) return fromObj;
  }

  if (id) {
    const cached = slugCacheByCreatorId.get(String(id));
    if (cached) return cached;
    const stored = readCreatorStorefrontFromStorage(id);
    const fromStored = normalizeCreatorSlugInput(stored?.creatorSlug);
    if (fromStored) return fromStored;
  }

  return '';
}

export function enrichCreatedByForShopLink(createdBy) {
  if (!createdBy) return createdBy;
  if (typeof createdBy !== 'object') {
    const slug = resolveCreatorSlug(createdBy);
    return slug ? { _id: createdBy, creatorSlug: slug } : createdBy;
  }
  const slug = resolveCreatorSlug(createdBy);
  if (!slug || normalizeCreatorSlugInput(createdBy.creatorSlug) === slug) return createdBy;
  return { ...createdBy, creatorSlug: slug };
}

/** Keep in sync with backend RESERVED_CREATOR_SLUGS + static storefront routes */
export const RESERVED_STOREFRONT_SLUGS = new Set([
  'about',
  'account',
  'admin',
  'api',
  'assets',
  'auth',
  'bag',
  'brand',
  'cart',
  'categories',
  'category',
  'checkout',
  'contact',
  'creator',
  'creators',
  'forgot-password',
  'health',
  'help',
  'home',
  'login',
  'logout',
  'manifest',
  'favicon',
  'order',
  'orders',
  'payment-methods',
  'privacy',
  'privacy-policy',
  'product',
  'products',
  'profile',
  'public',
  'register',
  'reset-password',
  'return-policy',
  'search',
  'settings',
  'shop',
  'signup',
  'static',
  'sw92team',
  'tag',
  'terms',
  'wishlist',
]);

export const STOREFRONT_SEGMENT_MODE_EVENT = 'sheenwear:storefront-segment-mode';

export function dispatchStorefrontSegmentMode(mode, segment = '') {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent(STOREFRONT_SEGMENT_MODE_EVENT, {
        detail: { mode, segment: String(segment || '') },
      })
    );
  } catch {
    /* ignore */
  }
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function normalizeCreatorSlugInput(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function validateCreatorSlug(slug) {
  const s = normalizeCreatorSlugInput(slug);
  if (!s) return { valid: true, slug: '', error: null };
  if (s.length < 3 || s.length > 40) {
    return { valid: false, slug: s, error: 'Shop URL must be 3–40 characters.' };
  }
  if (!SLUG_PATTERN.test(s)) {
    return {
      valid: false,
      slug: s,
      error: 'Use lowercase letters, numbers, and hyphens only.',
    };
  }
  if (RESERVED_STOREFRONT_SLUGS.has(s)) {
    return { valid: false, slug: s, error: 'This shop URL is reserved. Choose another.' };
  }
  return { valid: true, slug: s, error: null };
}

export function isReservedStorefrontSlug(slug) {
  if (!slug) return true;
  return RESERVED_STOREFRONT_SLUGS.has(String(slug).trim().toLowerCase());
}

export function isMongoObjectIdString(value) {
  return /^[a-f0-9]{24}$/i.test(String(value || '').trim());
}

/** Path segment for public shop URL — slug preferred, else MongoDB id. */
export function buildCreatorShopPathSegment(createdByOrId) {
  if (createdByOrId == null || createdByOrId === '') return null;
  const slug = resolveCreatorSlug(createdByOrId);
  if (slug) return encodeURIComponent(slug);
  const id =
    typeof createdByOrId === 'string'
      ? createdByOrId.trim()
      : extractCreatorId(createdByOrId);
  if (id) return encodeURIComponent(String(id));
  return null;
}

/** Public shop URL: `/umardaraz` or `/{mongoId}` when no handle yet. */
export function buildCreatorShopPath(createdBy) {
  const segment = buildCreatorShopPathSegment(createdBy);
  return segment ? `/${segment}` : null;
}

/** Alias for share/link helpers (same as buildCreatorShopPath). */
export function shopPath(creator) {
  return buildCreatorShopPath(creator);
}

/** Prefer canonical slug URL for links (PDP, share, admin preview). */
export function getCreatorShopLinkPath(createdBy) {
  const enriched = enrichCreatedByForShopLink(createdBy);
  return (
    buildCanonicalCreatorShopPath(enriched) || buildCreatorShopPath(enriched)
  );
}

/** Canonical handle URL `/{slug}` — null until slug is known. */
export function buildCanonicalCreatorShopPath(createdByOrId) {
  const slug = resolveCreatorSlug(createdByOrId);
  return slug ? `/${encodeURIComponent(slug)}` : null;
}

export function getCreatorIdentifierFromLegacyShopPath(pathname) {
  const m = String(pathname || '').match(/^\/creators?\/([^/?#]+)/i);
  return m ? decodeURIComponent(m[1]) : '';
}

export function getCreatorIdentifierFromRootShopPath(pathname) {
  const seg = getSingleSegmentFromPath(pathname);
  if (!seg || isReservedStorefrontSlug(seg)) return '';
  return seg;
}

export function getCreatorIdentifierFromShopPath(pathname) {
  return (
    getCreatorIdentifierFromLegacyShopPath(pathname) ||
    getCreatorIdentifierFromRootShopPath(pathname)
  );
}

export function creatorProductMatchesIdentifier(createdBy, identifier, canonicalCreatorId) {
  if (!identifier) return false;
  const ident = String(identifier).trim();
  const identLower = ident.toLowerCase();
  const id = extractCreatorId(createdBy);
  if (id && String(id) === ident) return true;
  if (canonicalCreatorId && id && String(id) === String(canonicalCreatorId)) {
    if (identLower === String(canonicalCreatorId).toLowerCase()) return true;
  }
  if (typeof createdBy === 'object' && createdBy.creatorSlug) {
    if (normalizeCreatorSlugInput(createdBy.creatorSlug) === identLower) return true;
  }
  if (isMongoObjectIdString(ident) && id && String(id).toLowerCase() === identLower) return true;
  if (!isMongoObjectIdString(ident) && identLower === ident && typeof createdBy === 'object') {
    const slug = normalizeCreatorSlugInput(createdBy.creatorSlug);
    if (slug && slug === identLower) return true;
  }
  return false;
}

export function getSingleSegmentFromPath(pathname) {
  const path = String(pathname || '').replace(/\/+$/, '') || '/';
  const m = path.match(/^\/([^/?#]+)$/);
  return m ? decodeURIComponent(m[1]) : '';
}

export function isLegacyCreatorShopPath(pathname) {
  return /^\/creators?\/[^/]+$/i.test(String(pathname || ''));
}

/** @deprecated use isLegacyCreatorShopPath */
export function isLegacyCreatorPath(pathname) {
  return isLegacyCreatorShopPath(pathname);
}

/** True for legacy `/creator/x` or root `/handle` (not reserved; may still be a product slug at runtime). */
export function isCreatorShopPath(pathname) {
  if (isLegacyCreatorShopPath(pathname)) return true;
  const seg = getSingleSegmentFromPath(pathname);
  if (!seg) return false;
  if (isReservedStorefrontSlug(seg)) return false;
  if (isMongoObjectIdString(seg)) return true;
  return false;
}

export function extractCreatorIdentifierFromPath(pathname) {
  return getCreatorIdentifierFromShopPath(pathname);
}
